import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { formatUnknownError, getResponseDebugContext, readResponseJson } from '@/util/api/response-body'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'

type AgentV2RequestArgs<T> = {
  body?: BodyInit
  method: 'DELETE' | 'GET' | 'PATCH' | 'POST' | 'PUT'
  parseResponse: (data: unknown, context: AgentV2ResponseContext) => T
  sentryEndpoint: string
  sentryExtra?: Record<string, unknown>
  signal?: AbortSignal
  url: string
  allowNoContent?: boolean
  expectedErrorStatuses?: readonly number[]
}

export type AgentV2ResponseContext = {
  response: Response
  sentryTags: {
    endpoint: string
    transaction_id: string
  }
}

export async function agentV2Request<T>(args: AgentV2RequestArgs<T>): Promise<T> {
  const sentryTransactionId = nanoid()
  const sentryTags = {
    endpoint: args.sentryEndpoint,
    transaction_id: sentryTransactionId,
  }

  try {
    const token = await getAuth().currentUser?.getIdToken()
    if (!token) throw new Error('User not authenticated')

    const headers: Record<string, string> = {
      'X-Transaction-Id': sentryTransactionId,
      Authorization: `Bearer ${token}`,
    }
    // A FormData body needs the browser to set Content-Type so it carries the multipart boundary.
    if (!(args.body instanceof FormData)) headers['Content-Type'] = 'application/json; charset=UTF-8'

    const requestInit: RequestInit = {
      method: args.method,
      signal: args.signal,
      headers,
    }
    if (args.body !== undefined) requestInit.body = args.body

    const response = await fetch(args.url, requestInit)
    const { data, parseError } = args.allowNoContent && response.status === 204 ? { data: undefined, parseError: null } : await readResponseJson(response)

    if (!response.ok) {
      const errorData = parseError ? {} : data
      const detail = formatAgentV2ErrorDetail(errorData)
      if (!args.signal?.aborted && !args.expectedErrorStatuses?.includes(response.status)) {
        Sentry.withScope((scope) => {
          scope.setTags(sentryTags)
          Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
            extra: {
              ...getResponseDebugContext(response, args.url),
              ...args.sentryExtra,
              responseData: errorData,
              parseError: parseError ? formatUnknownError(parseError) : null,
            },
          })
        })
      }
      throw new ApiError(response.status, detail || `Agent v2 request failed: ${response.status}`, (errorData as { detail?: unknown } | undefined)?.detail)
    }

    if (parseError) {
      if (!args.signal?.aborted) {
        Sentry.withScope((scope) => {
          scope.setTags(sentryTags)
          Sentry.captureException(new Error('Agent v2 response JSON parse failed'), {
            extra: {
              ...getResponseDebugContext(response, args.url),
              ...args.sentryExtra,
              parseError: formatUnknownError(parseError),
            },
          })
        })
      }
      throw new ApiResponsePayloadError(response.status, 'Invalid Agent v2 response JSON')
    }

    return args.parseResponse(data, { response, sentryTags })
  } catch (error) {
    if (!args.signal?.aborted && !(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags(sentryTags)
        Sentry.captureException(error, { extra: args.sentryExtra })
      })
    }
    throw error
  }
}

function formatAgentV2ErrorDetail(data: unknown): string {
  const responseData = data as { detail?: unknown } | undefined
  const detail = responseData?.detail ?? data
  if (typeof detail === 'string') return detail
  if (detail == null) return ''
  return JSON.stringify(detail)
}
